import { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { useChatContext } from "./ChatContext";
import ChatMessage from "./ChatMessage";

const pulse = keyframes`
    0% { box-shadow: 0 0 0 0 rgba(36, 50, 177, 0.5); }
    70% { box-shadow: 0 0 0 12px rgba(36, 50, 177, 0); }
    100% { box-shadow: 0 0 0 0 rgba(36, 50, 177, 0); }
`;

const slideUp = keyframes`
    from { opacity: 0; transform: translateY(20px) scale(0.95); }
    to { opacity: 1; transform: translateY(0) scale(1); }
`;

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

/* ── Floating Button ─── */

const ChatButton = styled.button<{ $pulse: boolean }>`
    position: fixed;
    bottom: 24px;
    right: 24px;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: linear-gradient(135deg, #2432b1 0%, #1a3299 100%);
    border: none;
    cursor: pointer;
    z-index: 999;
    box-shadow: 0 4px 20px rgba(36, 50, 177, 0.4);
    transition: transform 0.2s, box-shadow 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: ${(props) => (props.$pulse ? pulse : "none")} 2s infinite;

    &:hover {
        transform: scale(1.08);
        box-shadow: 0 6px 28px rgba(36, 50, 177, 0.5);
    }
`;

const ActivioIcon = () => (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <circle cx="14" cy="14" r="12" stroke="white" strokeWidth="2" />
        <line x1="14" y1="8" x2="14" y2="20" stroke="white" strokeWidth="2" strokeLinecap="round" />
        <line x1="8" y1="14" x2="20" y2="14" stroke="white" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

/* ── Chat Window ─── */

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: 999;
    animation: ${fadeIn} 0.2s ease-out;
    display: none;

    @media (max-width: 768px) {
        display: block;
    }
`;

const ChatWindow = styled.div<{ $isMobile: boolean }>`
    position: fixed;
    ${(props) =>
        props.$isMobile
            ? `
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        border-radius: 0;
    `
            : `
        bottom: 100px;
        right: 24px;
        width: 400px;
        height: 600px;
        border-radius: 20px;
    `}
    background: #f0f4ff;
    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.2);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: ${slideUp} 0.3s ease-out;
`;

/* ── Header ─── */

const ChatHeader = styled.div`
    background: linear-gradient(135deg, #2432b1 0%, #1a3299 100%);
    color: white;
    padding: 16px 20px;
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
`;

const HeaderAvatar = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.15);
    border: 2px solid rgba(255, 255, 255, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const HeaderInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const HeaderName = styled.div`
    font-size: 16px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const OnlineDot = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #4ade80;
    display: inline-block;
`;

const HeaderSub = styled.div`
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
    margin-top: 2px;
`;

const CloseButton = styled.button`
    background: rgba(255, 255, 255, 0.1);
    border: none;
    color: white;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;
    flex-shrink: 0;
    padding: 0;

    &:hover {
        background: rgba(255, 255, 255, 0.2);
    }
`;

/* ── Messages ─── */

const MessagesContainer = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 20px 16px;
    display: flex;
    flex-direction: column;
    gap: 4px;

    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.1);
        border-radius: 2px;
    }
`;

const WelcomeContainer = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 8px;
`;

const WelcomeAvatar = styled.div`
    width: 32px;
    height: 32px;
    min-width: 32px;
    border-radius: 50%;
    background: linear-gradient(135deg, #2432b1 0%, #1a3299 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 2px;
`;

const WelcomeBubble = styled.div`
    background: white;
    border-radius: 16px;
    border-top-left-radius: 4px;
    padding: 14px 16px;
    font-size: 14px;
    line-height: 1.55;
    color: #1e293b;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    max-width: 85%;
`;

/* ── Suggestion Chips ─── */

const ChipsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px 0;
`;

const Chip = styled.button`
    background: white;
    border: 1.5px solid #d4daf0;
    border-radius: 20px;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 500;
    color: #2432b1;
    cursor: pointer;
    transition: all 0.2s;
    font-family: inherit;

    &:hover {
        background: #2432b1;
        color: white;
        border-color: #2432b1;
    }
`;

/* ── Typing Indicator ─── */

const typingDots = keyframes`
    0%, 20% { opacity: 0.3; }
    50% { opacity: 1; }
    80%, 100% { opacity: 0.3; }
`;

const TypingContainer = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
`;

const TypingBubble = styled.div`
    background: white;
    border-radius: 16px;
    border-top-left-radius: 4px;
    padding: 14px 18px;
    display: flex;
    gap: 4px;
    align-items: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
`;

const TypingDot = styled.span<{ $delay: number }>`
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #94a3b8;
    animation: ${typingDots} 1.4s infinite;
    animation-delay: ${(props) => props.$delay}s;
`;

/* ── Input Area ─── */

const InputArea = styled.div`
    padding: 12px 16px 8px;
    background: white;
    border-top: 1px solid #e8ecf4;
`;

const InputWrapper = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
`;

const Input = styled.input`
    flex: 1;
    padding: 12px 16px;
    border: 1.5px solid #e2e8f0;
    border-radius: 24px;
    font-size: 14px;
    font-family: inherit;
    outline: none;
    color: #1e293b;
    background: #f8fafc;
    transition: border-color 0.2s;

    &::placeholder {
        color: #94a3b8;
    }

    &:focus {
        border-color: #2432b1;
        background: white;
    }
`;

const SendButton = styled.button`
    background: #2432b1;
    color: white;
    border: none;
    border-radius: 50%;
    width: 42px;
    height: 42px;
    min-width: 42px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s, transform 0.15s;

    &:hover {
        background: #1a2690;
        transform: scale(1.05);
    }

    &:disabled {
        background: #cbd5e1;
        cursor: not-allowed;
        transform: none;
    }
`;

const Footer = styled.div`
    padding: 8px 16px 12px;
    text-align: center;
    font-size: 11px;
    color: #94a3b8;
    background: white;
`;

/* ── Constants ─── */

const WELCOME_MESSAGE =
    "¡Hola! Soy Activio \uD83D\uDC4B Tu asistente de inversión de Miactivo. Puedo explicarte cómo funcionan los tokens, las rentas en dólares, la regulación y los proyectos abiertos. ¿En qué te ayudo?";

const SUGGESTION_CHIPS = [
    "¿Desde cuánto invierto?",
    "¿Es seguro?",
    "¿Cómo retiro?",
];

/* ── Component ─── */

export default function ChatBubble(): JSX.Element {
    const { isOpen, messages, isLoading, openChat, closeChat, sendMessage } =
        useChatContext();
    const [inputText, setInputText] = useState("");
    const [showPulse, setShowPulse] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const hasSeenChat = localStorage.getItem("miactivo_chat_seen");
        if (!hasSeenChat) {
            setShowPulse(true);
            localStorage.setItem("miactivo_chat_seen", "true");
        }
    }, []);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isLoading]);

    const handleSend = async () => {
        if (!inputText.trim() || isLoading) return;
        const text = inputText.trim();
        setInputText("");
        try {
            await sendMessage(text);
        } catch {
            // Error is handled in ChatContext
        }
    };

    const handleChipClick = async (question: string) => {
        try {
            await sendMessage(question);
        } catch {
            // Error is handled in ChatContext
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (!isOpen) {
        return (
            <ChatButton onClick={openChat} $pulse={showPulse} aria-label="Abrir chat con Activio">
                <ActivioIcon />
            </ChatButton>
        );
    }

    const showWelcome = messages.length === 0;
    const showChips = messages.length === 0 || messages.length <= 2;

    return (
        <>
            {isMobile && <Overlay onClick={closeChat} />}
            <ChatWindow $isMobile={isMobile}>
                <ChatHeader>
                    <HeaderAvatar>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <line x1="10" y1="4" x2="10" y2="16" stroke="white" strokeWidth="2" strokeLinecap="round" />
                            <line x1="4" y1="10" x2="16" y2="10" stroke="white" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </HeaderAvatar>
                    <HeaderInfo>
                        <HeaderName>
                            Activio <OnlineDot />
                        </HeaderName>
                        <HeaderSub>Asistente de inversión IA</HeaderSub>
                    </HeaderInfo>
                    <CloseButton onClick={closeChat} aria-label="Cerrar chat">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M1 1l12 12M13 1L1 13" stroke="white" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </CloseButton>
                </ChatHeader>

                <MessagesContainer>
                    {showWelcome && (
                        <WelcomeContainer>
                            <WelcomeAvatar>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <line x1="8" y1="3" x2="8" y2="13" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                    <line x1="3" y1="8" x2="13" y2="8" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </WelcomeAvatar>
                            <WelcomeBubble>{WELCOME_MESSAGE}</WelcomeBubble>
                        </WelcomeContainer>
                    )}

                    {messages.map((msg) => (
                        <ChatMessage
                            key={msg.id}
                            direction={msg.direction}
                            body={msg.body}
                            isActivio={msg.isActivio}
                            sentAt={msg.sentAt}
                        />
                    ))}

                    {isLoading && (
                        <TypingContainer>
                            <WelcomeAvatar>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <line x1="8" y1="3" x2="8" y2="13" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                    <line x1="3" y1="8" x2="13" y2="8" stroke="white" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </WelcomeAvatar>
                            <TypingBubble>
                                <TypingDot $delay={0} />
                                <TypingDot $delay={0.2} />
                                <TypingDot $delay={0.4} />
                            </TypingBubble>
                        </TypingContainer>
                    )}

                    <div ref={messagesEndRef} />
                </MessagesContainer>

                {showChips && !isLoading && (
                    <ChipsContainer style={{ padding: "0 16px 8px" }}>
                        {SUGGESTION_CHIPS.map((chip, idx) => (
                            <Chip key={idx} onClick={() => handleChipClick(chip)}>
                                {chip}
                            </Chip>
                        ))}
                    </ChipsContainer>
                )}

                <InputArea>
                    <InputWrapper>
                        <Input
                            type="text"
                            placeholder="Escribí tu pregunta..."
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={isLoading}
                        />
                        <SendButton
                            onClick={handleSend}
                            disabled={!inputText.trim() || isLoading}
                            aria-label="Enviar mensaje"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                <path d="M5 12h14M13 5l7 7-7 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </SendButton>
                    </InputWrapper>
                </InputArea>

                <Footer>Activio puede cometer errores. Verificá la info importante.</Footer>
            </ChatWindow>
        </>
    );
}
